/* WAVY-JADE-0925: product card swipe gallery.
   Markup: snippets/card-product.liquid ([data-wj-cg] > [data-wj-cg-track] > a.wj-cg__slide).
   The track is a native scroll-snap scroller, so touch swipe needs no JS. This file adds dot sync, arrow buttons,
   arrow keys, mouse drag on fine pointers, and a polite "Photo n of N" status after button moves.
   Every listener is delegated from document, so cards injected later (facets.js, the jade rail, recommendations)
   work with no init. "wj:grid-updated" and section loads only re-sync the dots of cards already scrolled. */
(() => {
  'use strict';
  if (window.wjCardGalleryInstalled) return;
  window.wjCardGalleryInstalled = true;

  const GALLERY = '[data-wj-cg]';
  const TRACK = '[data-wj-cg-track]';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const state = new WeakMap();
  let suppressClick = false;

  const slides = (track) => track.children.length;
  const clamp = (i, n) => Math.max(0, Math.min(n - 1, i));

  function current(track) {
    const w = track.clientWidth;
    return w ? clamp(Math.round(Math.abs(track.scrollLeft) / w), slides(track)) : 0;
  }

  function get(gallery) {
    let s = state.get(gallery);
    if (!s) {
      s = { index: 0, announce: false, timer: 0 };
      state.set(gallery, s);
    }
    return s;
  }

  function sync(gallery, force) {
    const track = gallery.querySelector(TRACK);
    if (!track) return;
    const s = get(gallery);
    const n = slides(track);
    const i = current(track);
    if (i === s.index && !force) return;
    s.index = i;
    gallery.querySelectorAll('.wj-cg__dot').forEach((dot, k) => dot.classList.toggle('is-active', k === i));
    const prev = gallery.querySelector('[data-wj-cg-step="-1"]');
    const next = gallery.querySelector('[data-wj-cg-step="1"]');
    // WAVY-CRO-1001C: arrows wrap (last -> first), so both show on hover whenever a card has 2+ photos.
    if (prev) prev.setAttribute('aria-disabled', String(n < 2));
    if (next) next.setAttribute('aria-disabled', String(n < 2));
  }

  function announce(gallery) {
    const s = get(gallery);
    if (!s.announce) return;
    s.announce = false;
    const track = gallery.querySelector(TRACK);
    const status = gallery.querySelector('[data-wj-cg-status]');
    if (track && status) status.textContent = 'Photo ' + (current(track) + 1) + ' of ' + slides(track);
  }

  function goTo(gallery, index, speak) {
    const track = gallery.querySelector(TRACK);
    if (!track) return;
    const i = clamp(index, slides(track));
    if (speak) get(gallery).announce = true;
    track.scrollTo({ left: i * track.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
    if (i === current(track)) {
      sync(gallery, true);
      announce(gallery);
    }
  }

  function step(button, delta) {
    const gallery = button.closest(GALLERY);
    if (!gallery) return;
    if (button.getAttribute('aria-disabled') === 'true' && delta !== 0) return;
    const track = gallery.querySelector(TRACK);
    const n = slides(track);
    goTo(gallery, (current(track) + delta + n) % n, true); // WAVY-CRO-1001C: wrap
  }

  // Dot sync while the track scrolls (scroll does not bubble, so listen in the capture phase).
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target;
      if (!(track instanceof Element) || !track.matches(TRACK)) return;
      const gallery = track.closest(GALLERY);
      if (!gallery) return;
      sync(gallery);
      const s = get(gallery);
      clearTimeout(s.timer);
      s.timer = setTimeout(() => {
        sync(gallery);
        announce(gallery);
      }, 140);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'click',
    (event) => {
      // A mouse drag ends with a click on the slide link: swallow it so dragging never navigates.
      if (suppressClick && event.target.closest && event.target.closest(TRACK)) {
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const button = event.target.closest && event.target.closest('[data-wj-cg-step]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      step(button, Number(button.dataset.wjCgStep));
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    const button = event.target.closest && event.target.closest('[data-wj-cg-step]');
    if (!button) return;
    const gallery = button.closest(GALLERY);
    const track = gallery && gallery.querySelector(TRACK);
    if (!track) return;
    const keys = { ArrowLeft: -1, ArrowRight: 1 };
    if (event.key in keys) {
      event.preventDefault();
      const n = slides(track);
      goTo(gallery, (current(track) + keys[event.key] + n) % n, true); // WAVY-CRO-1001C: wrap, same as clicks
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      goTo(gallery, event.key === 'Home' ? 0 : slides(track) - 1, true);
    }
  });

  // Mouse drag (touch and pens use native scrolling). Snap is paused while dragging, then the track settles
  // on the nearest photo, biased one step in the drag direction once the drag passes 15% of the width.
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const track = event.target.closest && event.target.closest(TRACK);
    const gallery = track && track.closest(GALLERY);
    if (!gallery) return;
    const startX = event.clientX;
    const startLeft = track.scrollLeft;
    let dragging = false;

    const move = (e) => {
      const dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < 6) return;
        dragging = true;
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - dx;
    };
    const end = (e) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (!dragging) return;
      suppressClick = e.type === 'pointerup';
      setTimeout(() => {
        suppressClick = false;
      }, 0);
      track.classList.remove('is-dragging');
      const w = track.clientWidth || 1;
      const dx = e.clientX - startX;
      let target = Math.round(startLeft / w);
      if (Math.abs(dx) > w * 0.15) target += dx < 0 ? 1 : -1;
      goTo(gallery, target, false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  });

  // Browsers fire a native drag on links/images before our pointer logic; keep it off the track.
  document.addEventListener('dragstart', (event) => {
    if (event.target.closest && event.target.closest(TRACK)) event.preventDefault();
  });

  function resync(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(GALLERY).forEach((gallery) => sync(gallery, true));
  }

  // WAVY-JADE-0926: iOS Safari only applies :active (the card and quick-add press feedback in wavy-jade.css) once
  // some touchstart listener exists on the page.
  document.addEventListener('touchstart', () => {}, { passive: true });

  document.addEventListener('wj:grid-updated', (event) => resync(event.target));
  document.addEventListener('shopify:section:load', (event) => resync(event.target));
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resync(document);
  });
})();
